import { FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { Formik, Form } from "formik";

import Button from "../Button/Button";
import { FormikInputField } from "../FormikField";
import { inviteMemberSchema } from "../../utils/validators";
import { useSendInvitation } from "../../hooks/useSendInvitation";
import { useInvitations } from "../../hooks/useInvitations";
import { useMembers } from "../../hooks/useMembers";
import { selectAuthUser, selectAuthIsMember } from "../../store/selectors/auth";
import { selectAuthAccountId } from "../../store/selectors/auth";
import { supabase } from "@/lib/supabase";

import styles from "./AddMemberSection.module.css";

const AddMemberSection: FC = () => {
    const { submit, loading, error, success } = useSendInvitation();
    const { invitations, error: listError, refetch, revoke, revokingId, resend, resendingId } = useInvitations();
    const { members, error: membersError, remove, removingId, refetch: refetchMembers } = useMembers();
    const currentUser = useSelector(selectAuthUser);
    const accountId = useSelector(selectAuthAccountId);
    const isMember = useSelector(selectAuthIsMember);
    const isOwner = !isMember;

    const handleSubmit = async (email: string) => {
        const result = await submit(email);
        if (result.ok) {
            void refetch();
            void refetchMembers();
        }
    };

    const handleRevoke = async (inv: Parameters<typeof revoke>[0]) => {
        await revoke(inv);
        void refetchMembers();
    };

    const handleResend = async (inv: Parameters<typeof resend>[0]) => {
        await resend(inv);
        void refetchMembers();
    };

    // Real-time: listen for member-joined broadcast + refetch on tab focus
    useEffect(() => {
        let channel: ReturnType<typeof supabase.channel> | null = null;

        if (accountId) {
            const channelName = `account-updates:${accountId}`;
            channel = supabase
                .channel(channelName)
                .on("broadcast", { event: "member-joined" }, () => {
                    void refetch();
                    void refetchMembers();
                })
                .subscribe();
        }

        const onVisible = () => {
            if (document.visibilityState === "visible") {
                void refetch();
                void refetchMembers();
            }
        };
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            if (channel) void supabase.removeChannel(channel);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [accountId, refetch, refetchMembers]);

    return (
        <div className={styles.section}>
            <p className={styles.section__description}>
                Invite a team member by email. They will receive an invitation link to join your account.
            </p>

            <Formik
                initialValues={{ email: "" }}
                validationSchema={inviteMemberSchema}
                onSubmit={async (values) => {
                    await handleSubmit(values.email);
                }}
            >
                {() => (
                    <Form className={styles.invite} autoComplete="off">
                        <div className={styles.invite__field}>
                            <FormikInputField
                                name="email"
                                placeholder="Email"
                                type="email"
                            />
                        </div>
                        <Button
                            type="submit"
                            label={loading ? "Sending…" : "Send invitation"}
                            typeStyle="fill-dark"
                            size="lg"
                            className={styles.invite__submit}
                            disabled={loading}
                        />
                    </Form>
                )}
            </Formik>

            <div className={styles.messages}>
                {error && <p className={styles.error}>{error}</p>}
                {success && <p className={styles.success}>Invitation sent successfully.</p>}
            </div>

            {membersError && <p className={styles.error}>{membersError}</p>}

            {members.length > 0 && (
                <section className={styles.membersSection} aria-labelledby="members-heading">
                    <h2 id="members-heading" className={styles.invitationsHeading}>
                        Members
                    </h2>
                    <div className={styles.listWrap}>
                        <div className={styles.membersList}>
                            <div className={styles.list__header}>
                                <span>Name</span>
                                <span>Email</span>
                                <span>Role</span>
                                <span>Joined</span>
                                <span />
                            </div>
                            {members.map((m) => {
                                const isCurrentUser = currentUser?.id === m.user_id;
                                const isOwnerRole = m.role === "owner";
                                return (
                                    <div key={m.id} className={styles.list__row}>
                                        <span className={styles.list__name} data-label="Name" title={m.full_name || undefined}>
                                            {m.full_name || "—"}
                                            {isCurrentUser && (
                                                <span className={styles.youBadge}>(you)</span>
                                            )}
                                        </span>
                                        <span className={styles.list__email} data-label="Email" title={m.email ?? m.user_id}>
                                            {m.email ?? m.user_id}
                                        </span>
                                        <span className={styles.list__status} data-label="Role">
                                            {m.role}
                                        </span>
                                        <span className={styles.list__date} data-label="Joined">
                                            {new Date(m.joined_at).toLocaleDateString()}
                                        </span>
                                        <span className={styles.list__action} data-label="">
                                            {/* Временно скрыто: ошибка 500
                                            {!isOwner && (
                                                <button
                                                    type="button"
                                                    className={styles.list__transfer}
                                                    onClick={() => transfer(m)}
                                                    disabled={!!transferringId || removingId === m.id}
                                                >
                                                    {transferringId === m.user_id ? "Transferring…" : "Make Owner"}
                                                </button>
                                            )}
                                            */}
                                            {isOwner && !isOwnerRole && !isCurrentUser && (
                                                <button
                                                    type="button"
                                                    className={styles.list__revoke}
                                                    onClick={() => remove(m)}
                                                    disabled={removingId === m.id}
                                                >
                                                    {removingId === m.id ? "Removing…" : "Remove"}
                                                </button>
                                            )}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {listError && <p className={styles.error}>{listError}</p>}

            {invitations.length > 0 && (
                <section className={styles.invitationsSection} aria-labelledby="invitations-heading">
                    <h2 id="invitations-heading" className={styles.invitationsHeading}>
                        Invitations
                    </h2>
                    <div className={styles.listWrap}>
                        <div className={styles.list}>
                            <div className={styles.list__header}>
                                <span>Email</span>
                                <span>Status</span>
                                <span>Sent</span>
                                <span />
                            </div>
                            {invitations.map((inv) => (
                                <div key={inv.id} className={styles.list__row}>
                                    <span className={styles.list__email} data-label="Email" title={inv.email}>
                                        {inv.email}
                                    </span>
                                    <span className={styles.list__status} data-label="Status">
                                        {inv.status}
                                    </span>
                                    <span className={styles.list__date} data-label="Sent">
                                        {new Date(inv.created_at).toLocaleDateString()}
                                    </span>
                                    <span className={styles.list__action} data-label="">
                                        {inv.status === "pending" && (
                                            <>
                                                <button
                                                    type="button"
                                                    className={styles.list__resend}
                                                    onClick={() => handleResend(inv)}
                                                    disabled={resendingId === inv.id || revokingId === inv.id}
                                                >
                                                    {resendingId === inv.id ? "Resending…" : "Resend"}
                                                </button>
                                                {(isOwner || inv.invited_by === currentUser?.id) && (
                                                    <button
                                                        type="button"
                                                        className={styles.list__revoke}
                                                        onClick={() => handleRevoke(inv)}
                                                        disabled={revokingId === inv.id || resendingId === inv.id}
                                                    >
                                                        {revokingId === inv.id ? "Revoking…" : "Revoke"}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

export default AddMemberSection;
